import {useCallback, useEffect, useRef, type FocusEventHandler} from 'react'

export interface EstablishedAhpComposerFocusReturn {
  onFocusCapture: FocusEventHandler<HTMLDivElement>
  onBlurCapture: FocusEventHandler<HTMLDivElement>
}

export function useEstablishedAhpComposerFocusReturn(
  activeTakeoverKey: string | undefined,
  restoreFocus: () => void,
): EstablishedAhpComposerFocusReturn {
  const previousKeyRef = useRef<string | undefined>(undefined)
  const hadFocusRef = useRef(false)
  const restoreFocusRef = useRef(restoreFocus)

  useEffect(() => {
    restoreFocusRef.current = restoreFocus
  })

  useEffect(() => {
    const previousKey = previousKeyRef.current
    previousKeyRef.current = activeTakeoverKey

    if (!previousKey || activeTakeoverKey || !hadFocusRef.current) return

    hadFocusRef.current = false
    requestAnimationFrame(() => {
      if (document.activeElement !== document.body) return
      restoreFocusRef.current()
    })
  }, [activeTakeoverKey])

  const onFocusCapture = useCallback<FocusEventHandler<HTMLDivElement>>(() => {
    hadFocusRef.current = true
  }, [])

  const onBlurCapture = useCallback<FocusEventHandler<HTMLDivElement>>(event => {
    if (event.currentTarget.contains(event.relatedTarget)) return
    hadFocusRef.current = false
  }, [])

  return {onFocusCapture, onBlurCapture}
}
